import React, { useState } from 'react';
import { Box } from '@components/Box';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { GalleryECardModal } from '@components/GalleryComponents/GalleryECardModal/GalleryECardModal';
import {
  ContentWrapper,
  StyledGridContainer,
  StyledGridItem,
  StyledButtonNoneStyled,
  StyledPlaceholderCloudImage,
  TitleText,
  FooterContainer,
  FooterTitle,
  OutlinedBlackButton,
  HoverButton,
} from './DesignsSectionV2.styles';
const analytics = require('../../../../static/js/joy');
import resources from '@i18n/manifest';
import { useTranslation } from 'react-i18next';
import { WEBSITE_TEMPLATE_GALLERY_CARDS } from '@utils/designTemplates';

const TEMPLATES = [
  {
    id: 'DesignNelson',
    title: 'Nelson',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-nelson-photoduoright-v2.png',
    alt: 'Nelson Brannan',
  },
  {
    id: 'DesignMinimalistArchMocha',
    title: 'Minimalist Arch Mocha',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-minimalist_arch_mocha-photoarch-v2.png',
    alt: 'Minimalist Arch Mocha Brannan',
  },

  {
    id: 'DesignMagazine',
    title: 'Magazine',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-magazine-phototrio-v2.png',
    alt: 'Magazine Brannan',
  },
  {
    id: 'DesignLydia',
    title: 'Lydia',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-lydia-photobackground-v2.png',
    alt: 'Lydia Brannan',
  },
  {
    id: 'DesignArtistsGarden',
    themeId: 'artists_garden',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-artists_garden-center.png',
    alt: 'Artists Garden Brannan',
  },
  {
    id: 'DesignChloe',
    title: 'Chloe',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-chloe-photobackground-v2.png',
    alt: 'Chloe Brannan',
  },
  {
    id: 'DesignPomona',
    title: 'Pomona',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-pomona-photomarquee-v2.png',
    alt: 'Pomona Brannan',
  },
  {
    id: 'DesignRosebed',
    title: 'Rose Bed',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-rosebed-photobackground-v2.png',
    alt: 'Rosebed Brannan',
  },
  {
    id: 'DesignTourmaline',
    title: 'Tourmaline',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-tourmaline-photobackground-v2.png',
    alt: 'Tourmaline Brannan',
  },
  {
    id: 'DesignEmeraldCity',
    themeId: 'emrald_city',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-emerald_city-center-v2.png',
    alt: 'Emerald City Brannan',
  },
  {
    id: 'DesignFelicityOlive',
    title: 'Felicity Olive',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-felicity_olive-monogram-v2.png',
    alt: 'Felicity Olive Brannan',
  },
  {
    id: 'DesignCamelliaGarden',
    themeId: 'camellia_garden',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-camellia_garden-center-v2.png',
    alt: 'Camellia Garden Brannan',
  },
  {
    id: 'DesignClassicalCrestNavy',
    title: 'Classical Crest Navy',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-classical_crest_navy-monogram.png',
    alt: 'Classical Crest Navy Brannan',
  },
  {
    id: 'DesignIvoryGarden',
    themeId: 'ivory_garden',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-ivory-garden-center.png',
    alt: 'Ivory Garden Brannan',
  },
  {
    id: 'DesignQuince',
    title: 'Quince',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-quince-fullwidth.png',
    alt: 'Quince Brannan',
  },
  {
    id: 'DesignStoneAndGold',
    title: 'Stone & Gold',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-stone_and_gold-photobackground.png',
    alt: 'Stone And Gold Brannan',
  },
  {
    id: 'DesignCoastal',
    themeId: 'coastal',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-coastal-center.png',
    alt: 'Coastal Brannan',
  },
  {
    id: 'DesignStampPapaya',
    title: 'Stamp Papaya',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-stamp_papaya-monogram.png',
    alt: 'Stamp Papaya Brannan',
  },
  {
    id: 'DesignMountainGetaway',
    title: 'Mountain Getaway',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-mountain_getaway-photobackground.png',
    alt: 'Mountain Getaway Brannan',
  },
  {
    id: 'DesignMulberry',
    title: 'Mulberry',
    layout: 'brannan',
    src: 'wedding-website/design-gallery/brannan-mulberry-photoslider.png',
    alt: 'Mulberry Brannan',
  },
] as const;

interface DesignsSectionV2Props
  extends Readonly<{
    themes: MarketingData;
    sectionData: typeof resources.weddingWebsitePage.sections.designsV2;
  }> {}

export const DesignsSectionV2: React.FC<DesignsSectionV2Props> = ({ sectionData, themes }) => {
  const { t } = useTranslation('galleryPage');
  const productTypes = t('productTypes');
  const eCardDetailData = t('eCardDetailData');

  const sectionId = 'Design_Templates';
  const [isVisible, setIsVisible] = useState(false);
  const [isDisplayCardDetails, setCardDetailModalToggle] = useState(false);
  const { designs } = productTypes;
  const [cardDetails, setCardDetails] = useState<GalleryECard>({
    title: '',
    variationType: '',
    imageUrl: '',
    colors: [''],
    font: '',
    printUrl: '',
    tags: [''],
    vendor: '',
    productType: designs as GalleryProductType,
    variantColor: '',
    variantName: '',
    variantThemeId: '',
    theme: {
      name: '',
      themeId: '',
      marketingData: [],
    },
  });

  const handleCardClick = (search: { themeId?: string; title?: string }, layout: 'brannan' | 'aloha') => {
    let card: GalleryECard | undefined;

    if (search.themeId) {
      card = themes.find((theme) => theme.variantThemeId === search.themeId && theme.variationType === layout);
    }

    if (search.title) {
      card = WEBSITE_TEMPLATE_GALLERY_CARDS.find((card) => card.theme.name === search.title && card.variationType === layout);
    }

    if (typeof card !== 'undefined') {
      analytics.segment.sendTelemetryEvent('designBlockThemeClicked', {
        label: card.variantName ? card.variantName : card.theme?.name,
        value: 2,
        extraInfo: {
          version: 'weddingWebsiteDesignsSectionV2',
        },
      });
      setCardDetails(card);
      setCardDetailModalToggle(true);
    }
  };

  const handleCloseModal = () => {
    setCardDetailModalToggle(false);
  };

  return (
    <Box as={'section'} marginTop={[9, 9, 10]} marginBottom={[9, 9, 10]} style={{ position: 'relative', zIndex: 2 }} id={sectionId}>
      <LazyLoadWrapper beforeLoad={() => setIsVisible(true)} threshold={0}>
        <div />
      </LazyLoadWrapper>

      <ContentWrapper>
        <TitleText tagName={'h2'} variant={'featureHed17'} color={'neutra6'}>
          {sectionData.title}
        </TitleText>

        <StyledGridContainer>
          {TEMPLATES.map((template) => (
            <StyledGridItem key={template.id}>
              <StyledButtonNoneStyled
                id={template.id}
                skipRedirectWithTelemetry
                onClick={() =>
                  handleCardClick('themeId' in template ? { themeId: template.themeId } : { title: template.title }, template.layout)
                }
              >
                <StyledPlaceholderCloudImage
                  resizeWidth={450}
                  src={template.src}
                  alt={template.alt}
                  isMainSrcShown={isVisible}
                  optimization="default"
                />
                <HoverButton>
                  <span>{eCardDetailData.selectDesignButton} →</span>
                </HoverButton>
              </StyledButtonNoneStyled>
            </StyledGridItem>
          ))}
        </StyledGridContainer>

        <FooterContainer>
          <FooterTitle variant="featureHed17" tagName="span" color="neutra6">
            {sectionData.ctaTitle}
          </FooterTitle>
          <OutlinedBlackButton id="exploreMoreDesigns" forwardedAs={'a'} href="/designs">
            {sectionData.ctaText}
          </OutlinedBlackButton>
        </FooterContainer>
      </ContentWrapper>

      {isDisplayCardDetails && (
        <GalleryECardModal
          productType={designs as GalleryProductType}
          handleClose={handleCloseModal}
          cardDetails={cardDetails}
          isModalOpen={isDisplayCardDetails}
          isWeddingDesignSlider={true}
        />
      )}
    </Box>
  );
};
